import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './lib/api';
import { Login } from './routes/Login';
import { EmployeeDashboard } from './routes/EmployeeDashboard';
import { AdminDashboard } from './routes/AdminDashboard';
import { AdminRoster } from './routes/AdminRoster';
import { AdminPolicy } from './routes/AdminPolicy';
import { AdminLedger } from './routes/AdminLedger';

interface Me { tenant_id: string; user_id: string; role: string }

// Tiny location-based router. Replace with react-router in v3.1 if scope grows.
function useHashRoute(): string {
  const [path, setPath] = useState<string>(window.location.hash.slice(1) || '/');
  useEffect(() => {
    const on = () => setPath(window.location.hash.slice(1) || '/');
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return path;
}

export function App() {
  const [me, setMe] = useState<Me | null | 'loading'>('loading');
  const path = useHashRoute();

  useEffect(() => {
    api.get<Me>('/v1/auth/me')
      .then((m) => setMe(m))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setMe(null);
        else setMe(null);
      });
  }, []);

  const logout = useCallback(async () => {
    await api.post('/v1/auth/logout', {});
    setMe(null);
  }, []);

  if (me === 'loading') return <div className="min-h-screen flex items-center justify-center text-[color:var(--mute)]">Loading…</div>;
  if (me === null) return <Login />;

  const isAdmin = ['owner', 'admin', 'finance', 'manager'].includes(me.role);

  return (
    <div className="min-h-screen">
      <nav className="border-b border-[color:var(--rule)] px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <a href="#/" className="tracking-tight font-semibold">TknBudget</a>
          <div className="flex gap-6 text-sm text-[color:var(--mute)]">
            <a href="#/" className={path === '/' ? 'text-[color:var(--paper)]' : ''}>Dashboard</a>
            {isAdmin && <>
              <a href="#/admin" className={path === '/admin' ? 'text-[color:var(--paper)]' : ''}>Workspace</a>
              <a href="#/admin/roster" className={path.startsWith('/admin/roster') ? 'text-[color:var(--paper)]' : ''}>Roster</a>
              <a href="#/admin/policy" className={path.startsWith('/admin/policy') ? 'text-[color:var(--paper)]' : ''}>Policies</a>
              <a href="#/admin/ledger" className={path.startsWith('/admin/ledger') ? 'text-[color:var(--paper)]' : ''}>Ledger</a>
            </>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[color:var(--mute)] num">{me.user_id.slice(0, 8)}…</span>
          <button
            onClick={logout}
            className="text-xs text-[color:var(--mute)] hover:text-[color:var(--paper)] transition-colors"
          >
            Sign out
          </button>
        </div>
      </nav>
      <main>
        {path === '/' && <EmployeeDashboard />}
        {path === '/admin' && isAdmin && <AdminDashboard />}
        {path === '/admin/roster' && isAdmin && <AdminRoster />}
        {path === '/admin/policy' && isAdmin && <AdminPolicy />}
        {path === '/admin/ledger' && isAdmin && <AdminLedger />}
      </main>
    </div>
  );
}
